import {
  Box,
  Button,
  Grid,
  GridItem,
  Text,
  theme,
  useMediaQuery,
} from '@everlywell/ui-kit';
import Carousel from 'components/Carousel';
import { useCart } from 'components/Cart';
import RichTextWrapper from 'components/RichTextWrapper';
import { Link as GatsbyLink } from 'gatsby';
import React from 'react';
import { notEmpty } from 'utils/types/utilityTypes';

import ProductCard from './components/ProductCard';

export type CompareTestsSectionProps = {
  data: Queries.pdpSectionFragment;
  productData: Queries.pdpProductFragment;
};

/**
 * Section that displays the current product and two linked products to compare
 */
const CompareTestsSection = (props: CompareTestsSectionProps) => {
  const { data: section, productData } = props;

  const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.tablet})`);

  const currentProduct = productData;
  const linkedProducts = productData.linkedProducts?.filter(notEmpty) ?? [];

  const { addItemToCart, order, isAddItemToCartLoading } = useCart();
  const isLoading = !order || isAddItemToCartLoading;

  const handleAddToCart = (spreeProduct: Queries.pdpSpreeProductFragment) => {
    const variantId = spreeProduct.variants?.find(
      (variant) => variant?.is_master,
    )?.id;

    if (Number(variantId)) {
      addItemToCart({
        variant_id: Number(variantId),
        quantity: 1,
      });
    }
  };
  return (
    <Box>
      <Text
        width="100%"
        textAlign="center"
        color="viridian.base"
        marginBottom={{
          base: 1,
          desktop: 2,
        }}
      >
        {section.eyebrow}
      </Text>
      {section.body && (
        <Box width="100%" textAlign="center">
          <RichTextWrapper contentfulRichText={section.body} />
        </Box>
      )}
      <Box marginTop={{ base: 6, desktop: 10 }}>
        {isMobile ? (
          <Box
            sx={{
              '& .swiper-wrapper': {
                alignItems: 'stretch',
              },
              '& .swiper-slide': {
                height: 'auto',
              },
              '& > .swiper': {
                paddingBottom: '60px',

                '@media screen and (max-width: 768px)': {
                  paddingBottom: '24px',
                },
              },
            }}
          >
            <Carousel
              spaceBetween={16}
              slidesPerView={1}
              pagination={(currentProduct.linkedProducts?.length ?? 0) + 1 > 1}
              showPagination
              arrowPosition="bottom"
            >
              {[
                <CurrentProductCard
                  key={`product-card-${currentProduct.slug}`}
                  product={currentProduct}
                  handleOnClick={handleAddToCart}
                  isLoading={isLoading}
                />,
                ...[...linkedProducts]
                  .slice(0, 2)
                  .map((product, index: number) => (
                    <LinkedProductCard
                      key={`linked-product-card-${index}`}
                      product={product}
                    />
                  )),
              ]}
            </Carousel>
          </Box>
        ) : (
          <Grid
            columns={3}
            as="ul"
            sx={{ listStyle: 'none', justifyContent: 'center' }}
          >
            {/* Current product card */}
            <GridItem
              as="li"
              aria-label={currentProduct.spreeProduct?.name ?? ''}
            >
              <CurrentProductCard
                product={currentProduct}
                handleOnClick={handleAddToCart}
                isLoading={isLoading}
              />
            </GridItem>
            {/* Linked products cards */}
            {[...linkedProducts].slice(0, 2).map((product, index: number) => (
              <GridItem
                key={`grid-item-${index}`}
                as="li"
                aria-label={product.spreeProduct?.name ?? ''}
              >
                <LinkedProductCard key={product.slug} product={product} />
              </GridItem>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

const CurrentProductCard = (props: {
  product: Queries.pdpProductFragment;
  handleOnClick: (product: Queries.pdpSpreeProductFragment) => void;
  isLoading: boolean;
}) => {
  const { product: currentProduct, handleOnClick, isLoading } = props;

  const outOfStock =
    currentProduct.metadata?.flags?.find(
      (flag) => flag?.metadata?.metadata_id === 'outOfStock',
    )?.value === true;

  return (
    <ProductCard
      key={currentProduct.slug}
      product={currentProduct}
      isCurrentProduct
      cta={
        outOfStock ? (
          <Button size="sm" variant="primary" colorScheme="whiteCS" isDisabled>
            Out of stock
          </Button>
        ) : (
          <Button
            size="sm"
            variant="primary"
            colorScheme="whiteCS"
            isLoading={isLoading}
            onClick={() =>
              currentProduct.spreeProduct &&
              handleOnClick(currentProduct.spreeProduct)
            }
          >
            Add to cart
          </Button>
        )
      }
    />
  );
};

const LinkedProductCard = (props: {
  product: Queries.pdpContentfulProductFragment;
}) => {
  const { product } = props;

  return (
    <ProductCard
      key={product.slug}
      product={product}
      cta={
        <Button
          as={GatsbyLink}
          size="sm"
          variant="primary"
          colorScheme="whiteCS"
          to={`/products/${product.slug}`}
        >
          View more
        </Button>
      }
    />
  );
};

export default CompareTestsSection;
